import clsx from 'clsx';
import React, { useState } from 'react';

import { useCurrentUser } from '../../hooks';
import { Comment as CommentType, CommentableType } from '../../utils/backend-api/comments';
import CommentActions from './CommentActions';
import CommentAvatar from './CommentAvatar';
import CommentContent from './CommentContent';
import CommentFormSection from './CommentFormSection';
import CommentsFeed from './CommentsFeed';

const MAX_COMMENT_LEVEL = 2;

export default function Comment({
  comment,
  level = 1,
  setParentReplyingTo,
}: {
  comment: CommentType;
  level?: number;
  setParentReplyingTo?: (parentReplyingTo: string) => void;
}) {
  const [replyingTo, setReplyingTo] = useState<string>(null);
  const [isEditing, setIsEditing] = useState(false);
  const isReplyComment = level > 1;
  const { currentUser } = useCurrentUser();

  // Clicking reply on a MAX_COMMENT_LEVEL comment shows the form for its parent comment
  const replyToParent = level >= MAX_COMMENT_LEVEL;
  const commentableId = replyToParent ? comment.commentable_id : comment.id;
  const setReplyingToFormFunc = replyToParent ? setParentReplyingTo : setReplyingTo;
  const onEditComment = () => setIsEditing(true);
  const hideEditForm = () => setIsEditing(false);

  return (
    <div className="comment-container">
      <div
        className={clsx('comment-single', {
          '--reply': isReplyComment,
        })}
        id={`comment-${comment.id}`}
      >
        <div>
          <CommentAvatar user={comment.user} className="hidden sm:block" />
        </div>
        <div className="comment-main">
          {isEditing ? (
            <div className="comment-body">
              <CommentFormSection
                comment={comment}
                hideEditForm={hideEditForm}
                commentableId={commentableId}
                commentableType={CommentableType.Comment}
              />
            </div>
          ) : (
            <CommentContent comment={comment} />
          )}
          <CommentActions
            comment={comment}
            setReplyingTo={setReplyingToFormFunc}
            onEditComment={onEditComment}
          />
        </div>
      </div>
      {comment.child_comments && (
        <CommentsFeed
          comments={comment.child_comments}
          setParentReplyingTo={setReplyingTo}
          level={level + 1}
        />
      )}
      {level < MAX_COMMENT_LEVEL && replyingTo && currentUser?.permitted_to_comment && (
        <CommentFormSection
          commentableId={commentableId}
          commentableType={CommentableType.Comment}
          hideReplyForm={() => setReplyingTo(null)}
          replyingTo={replyingTo}
        />
      )}
      {!isReplyComment && <div className="comment-divider" />}
    </div>
  );
}
